import { Box, Button, Chip, Typography } from "@mui/material";
import React, { useEffect } from "react";
import FormComponent from "../Common/FormComponent/FormComponent";
import { seriesSelection } from "./constants";
import useForm from "../Common/FormComponent/useForm";
import useApi from "../../hooks/useApi";
import CommunicationHubApis from "../../services/api/CommunicationHub";
import RenderIf from "../Common/RenderIf/RenderIf";
import useOptions from "./hooks/useOptions";
import ToggleActive from "./ToggleActive";

const SeriesSelector = ({ title, onChange, defaultValue = {}, onDelete }) => {
  const form = useForm(defaultValue);

  const { values, errors } = form;

  const { options } = useOptions();
  const elementListApi = useApi({
    queryKey: [values?.ref_type],
    queryFn: () =>
      CommunicationHubApis.getList({
        element_type: values?.ref_type,
        page_request: {
          page: 1,
          page_size: 100,
        },
      }),
    initialData: {
      page_response: {
        previous_page: -1,
        next_page: -1,
        items_count: 20,
      },
      elements: [],
    },
    enabled: !!values?.ref_type,
  });

  const detailsApi = useApi({
    queryKey: [values?.ref_id],
    queryFn: () =>
      CommunicationHubApis.getDetail({
        element_type: values?.ref_type,
        element_id: values?.ref_id,
      }),
    enabled: !!values?.ref_id,
  });

  const elementsOptions = {
    ref_ids: elementListApi?.data?.elements?.map((item) => ({
      value: item.element_id,
      label: item.name,
    })),
  };

  const languages =
    detailsApi.data?.content?.map(
      (item) =>
        options?.languages?.find((lang) => lang.value === item.lang_code)?.label
    ) ?? [];

  useEffect(() => {
    onChange && onChange({ values, errors, detailsApi });
  }, [values, detailsApi.data, errors]);

  return (
    <Box
      maxWidth={750}
      borderBottom={"1px solid #D6D6D6"}
      position={"relative"}
    >
      <Button
        variant="text"
        color="error"
        onClick={onDelete}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
        }}
      >
        Delete
      </Button>
      <Box my={2}>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Box display={"flex"} flexDirection={"column"} gap={2} p={2}>
        {seriesSelection.map((config) => (
          <FormComponent
            key={config.id}
            config={config}
            form={form}
            otherOptions={
              elementsOptions[config?.props?.optionsKey ?? config.id]
            }
          />
        ))}

        <RenderIf condition={detailsApi?.data && !detailsApi?.data?.is_active}>
          <Box display={"flex"} gap={2} alignItems={"center"} ml="auto">
            <Typography color={"orange"}>
              The element you're adding is inactive
            </Typography>
            <ToggleActive
              data={detailsApi?.data}
              elementId={values?.ref_id}
              elementType={values?.ref_type}
              isActive={detailsApi?.data?.is_active}
              callback={detailsApi.refetch}
            />
          </Box>
        </RenderIf>
        <RenderIf condition={!!form.values.ref_id}>
          <Chip
            size="small"
            sx={{ width: "max-content", ml: "auto" }}
            label={form.values.ref_id}
          />
        </RenderIf>
        <RenderIf condition={detailsApi.isFetching}>Getting languages</RenderIf>
        <RenderIf condition={!detailsApi.isFetching && detailsApi?.data}>
          <Box display={"flex"} gap={1}>
            <Typography variant="body2" flex={1}>
              Languages:
            </Typography>
            {languages.map((item) => (
              <Chip key={item} label={item} color="info" variant="filled" />
            ))}
          </Box>
        </RenderIf>
      </Box>
    </Box>
  );
};

export default SeriesSelector;
